import React, { useState, useMemo } from 'react';
import { X, Calendar, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import StripePaymentForm from './StripePaymentForm';
import {
  resolvePaymentTerms,
  calculateRemainingPayments,
  rulesForPaymentCalculation,
} from '../../utils/paymentTerms';

const BalancePaymentModal = ({
  isOpen,
  onClose,
  booking,
  balanceAmount,
  payments = [],
  onPaymentSuccess,
  onPaymentError
}) => {
  const [cardholderName, setCardholderName] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentSchedule, setShowPaymentSchedule] = useState(true);
  const [payAllRemaining, setPayAllRemaining] = useState(true);

  // Calculate payment terms based on booking event date
  const paymentTerms = useMemo(() => {
    if (!booking?.event_date) return null;
    const subtotal = parseFloat(booking?.subtotal_amount || 0);
    const platformFee = parseFloat(booking?.platform_fee || 0);
    const rules = rulesForPaymentCalculation(
      booking?.vendor_service_package?.payment_schedule_rules ??
        booking?.package?.payment_schedule_rules
    );
    return resolvePaymentTerms(booking.event_date, subtotal, rules, { platformFee });
  }, [
    booking?.event_date,
    booking?.subtotal_amount,
    booking?.platform_fee,
    booking?.vendor_service_package?.payment_schedule_rules,
    booking?.package?.payment_schedule_rules,
  ]);

  // Get completed payments
  const completedPayments = useMemo(() => {
    return payments.filter(p => p.status === 'completed') || [];
  }, [payments]);

  // Remaining installments only — deposit was already paid at booking
  const remainingPayments = useMemo(() => {
    if (!paymentTerms || !booking) return [];
    const subtotal = parseFloat(booking.subtotal_amount || 0);
    const platformFee = parseFloat(booking.platform_fee || 0);
    const allRemaining = calculateRemainingPayments(
      paymentTerms,
      completedPayments,
      subtotal,
      platformFee
    );

    return allRemaining.filter((payment) => {
      const isDepositInstallment =
        payment.index === 0 || /deposit/i.test(payment.label || '');

      if (isDepositInstallment) return false;
      if ((payment.remainingAmount || 0) <= 0.01) return false;

      return true;
    });
  }, [paymentTerms, completedPayments, booking]);

  React.useEffect(() => {
    if (!isOpen) return;

    if (remainingPayments.length === 0) {
      setPayAllRemaining(true);
      setSelectedPayment(null);
      return;
    }

    if (!selectedPayment && !payAllRemaining) {
      setSelectedPayment(remainingPayments[0]);
    }
  }, [isOpen, remainingPayments, selectedPayment, payAllRemaining]);

  // Calculate selected payment amount
  const selectedPaymentAmount = useMemo(() => {
    if (payAllRemaining) {
      return balanceAmount; // Pay all remaining balance
    }
    if (!selectedPayment || !booking) return balanceAmount;
    return selectedPayment.remainingAmount || selectedPayment.amount;
  }, [selectedPayment, booking, balanceAmount, payAllRemaining]);

  const paymentType = useMemo(() => 'full_payment', []);

  // Memoize metadata - cardholderName can be included because metadata changes
  // won't trigger payment intent recreation (metadata is not in useEffect deps)
  const paymentMetadata = useMemo(() => ({
    cardholderName: cardholderName,
    onCardholderNameChange: setCardholderName,
    paymentLabel: payAllRemaining 
      ? 'Pay All Remaining Balance' 
      : (selectedPayment?.label || 'Balance Payment'),
    paymentPercentage: payAllRemaining 
      ? (() => {
          const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
          return totalDue > 0 ? (balanceAmount / totalDue) * 100 : null;
        })()
      : (selectedPayment?.percentage || null),
    // Send proportional platform fee information for backend calculation
    paymentPlatformFee: payAllRemaining
      ? (() => {
          const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
          const remainingPercentage = totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
          return (parseFloat(booking?.platform_fee || 0) * remainingPercentage) / 100;
        })()
      : (selectedPayment 
          ? (parseFloat(booking?.platform_fee || 0) * selectedPayment.percentage) / 100
          : null),
    totalPlatformFee: parseFloat(booking?.platform_fee || 0)
  }), [
    cardholderName,
    payAllRemaining,
    selectedPayment,
    balanceAmount,
    booking?.subtotal_amount,
    booking?.platform_fee
  ]);

  if (!isOpen || !booking || balanceAmount <= 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] overflow-y-auto" style={{ paddingTop: '80px', paddingBottom: '20px' }}>
      <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6 relative my-auto max-h-[calc(100vh-100px)] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-black mb-2">Pay Remaining Balance</h2>
          <p className="text-sm text-gray-600">
            Complete your payment for booking #{booking.booking_reference}
          </p>
        </div>

        {/* Payment Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Subtotal:</span>
            <span className="text-sm font-medium text-black">${parseFloat(booking?.subtotal_amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Platform Fee:</span>
            <span className="text-sm font-medium text-black">${parseFloat(booking?.platform_fee || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Total Amount:</span>
            <span className="text-sm font-medium text-black">
              ${(parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0)).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Amount Paid:</span>
            <span className="text-sm font-medium text-black">-${parseFloat(booking?.amount_paid || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-300">
            <span className="text-base font-bold text-black">Balance Due:</span>
            <span className="text-lg font-bold text-black">${balanceAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Schedule Selection */}
        {paymentTerms && balanceAmount > 0 && (
          <div className="border-2 border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 bg-blue-50">
            <button
              onClick={() => setShowPaymentSchedule(!showPaymentSchedule)}
              className="w-full flex items-center justify-between text-left mb-3 sm:mb-4"
            >
              <div className="flex items-center">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 sm:mr-3" />
                <h3 className="font-bold text-base sm:text-lg text-black">
                  Choose Payment Option
                </h3>
              </div>
              {showPaymentSchedule ? (
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              )}
            </button>
            
            {showPaymentSchedule && (
              <div className="space-y-3">
                <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4">
                  {remainingPayments.length > 0
                    ? 'Pay your full remaining balance now, or choose a scheduled installment below.'
                    : 'Pay the full remaining balance to complete your booking.'}
                </p>
                
                {/* Pay All Option */}
                <button
                  onClick={() => {
                    setPayAllRemaining(true);
                    setSelectedPayment(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                    payAllRemaining
                      ? 'border-green-600 bg-green-100'
                      : 'border-gray-200 bg-white hover:border-green-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-1">
                        <DollarSign className={`w-4 h-4 mr-2 flex-shrink-0 ${payAllRemaining ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-medium text-sm text-black">Pay All Remaining Balance</span>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">Pay the full remaining balance now</p>
                      {(() => {
                        const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
                        const remainingPercentage = totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
                        const proportionalFee = (parseFloat(booking?.platform_fee || 0) * remainingPercentage) / 100;
                        return (
                          <p className="text-xs text-gray-500 ml-6 mt-1">
                            Includes ${proportionalFee.toFixed(2)} platform fee
                          </p>
                        );
                      })()}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="font-bold text-sm text-black">
                        ${balanceAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </button>
                
                {remainingPayments.length > 0 && (
                  <div className="space-y-2">
                    {remainingPayments.map((payment) => {
                      const isSelected = !payAllRemaining && selectedPayment?.index === payment.index;
                      const proportionalFee = (parseFloat(booking?.platform_fee || 0) * payment.percentage) / 100;
                      
                      return (
                        <button
                          key={payment.index}
                          onClick={() => {
                            setSelectedPayment(payment);
                            setPayAllRemaining(false);
                          }}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                            isSelected
                              ? 'border-blue-600 bg-blue-100'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center mb-1 flex-wrap">
                                <DollarSign className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                                <span className="font-medium text-sm text-black">{payment.label}</span>
                                <span className="ml-2 text-xs text-gray-500">({payment.percentage}%)</span>
                              </div>
                              <p className="text-xs text-gray-600 ml-6 break-words">{payment.dueDate}</p>
                              <p className="text-xs text-gray-500 ml-6 mt-1">
                                Includes ${proportionalFee.toFixed(2)} platform fee
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="font-bold text-sm text-black">
                                ${payment.remainingAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stripe Payment Form */}
        <StripePaymentForm
          amount={selectedPaymentAmount}
          bookingId={booking.id}
          paymentType={paymentType}
          onSuccess={onPaymentSuccess}
          onError={onPaymentError}
          metadata={paymentMetadata}
        />
      </div>
    </div>
  );
};

export default BalancePaymentModal;

